<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    :create="create"
    title="One-Year Filing Deadline"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-alert
        border="left"
        colored-border
        color="#2A3B4D"
        icon="mdi-information-outline"
        elevation="2"
        class="mb-6"
      >
        Applications for Asylum must be filed within one year of date of last arrival unless an exception applies
      </v-alert>
      <v-row>
        <v-col
          cols="12"
        >
          To see or change <span class="font-weight-bold">Date of Last Arrival</span> below, please go to the Overview Tab
        </v-col>
        <v-col
          cols="12"
          sm="5"
        >
          <ComboDate
            :date="section.arrival_date"
            label="Date of last arrival"
            outlined
            :disabled="locked"
            :uncertain="false"
            hide-uncertain
            inactive
          />
        </v-col>
        <v-col
          cols="6"
          sm="2"
        >
          <h5>Calculated Filing</h5>
          <div class="text-h6">
            {{ calculatedFilingTime(section) }}
          </div>
        </v-col>
        <v-col
          cols="6"
          sm="4"
        >
          <div>
            <strong>Disclaimer: </strong>The calculated date is 365 days after the date of last arrival. It is not a substitute for individual analysis.
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          sm="5"
        >
          <h4>
            Is the application filed more than 1 year after last arrival?
          </h4>
          <v-radio-group
            v-model="section.filed_more_than_one_year"
            row
            class="mt-0"
          >
            <!-- eslint-disable vue/prefer-true-attribute-shorthand -->
            <v-radio
              label="Yes"
              :value="true"
            />
            <!-- eslint-enable -->
            <v-radio
              label="No"
              :value="false"
            />
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="float-left instruction-text"
        >
          <div>
            If an exception applies, add an additional module for improved analysis
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <div>
            <h3>One Year Filing Exception</h3>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          sm="5"
        >
          <h4>
            Does an exception apply?
          </h4>
          <v-radio-group
            v-model="section.does_an_exception_apply"
            row
            class="mt-0"
          >
            <!-- eslint-disable vue/prefer-true-attribute-shorthand -->
            <v-radio
              label="Yes"
              :value="true"
            />
            <!-- eslint-enable -->
            <v-radio
              label="No"
              :value="false"
            />
          </v-radio-group>
        </v-col>
        <v-col
          v-if="section.does_an_exception_apply"
          cols="12"
          sm="7"
        >
          <h4>
            Select Exception
          </h4>
          <v-radio-group
            v-model="section.exception_type"
            row
            class="mt-0"
          >
            <v-radio
              label="UAC"
              value="uac"
            />
            <v-radio
              label="Changed"
              value="changed"
            />
            <v-radio
              label="Extraordinary"
              value="extraordinary"
            />
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row v-if="section.does_an_exception_apply">
        <v-col cols="12">
          <v-textarea
            v-model="section.exception_basis"
            auto-grow
            outlined
            autocomplete="off"
            label="Describe the factual basis for the exception"
          />
        </v-col>
      </v-row>
      <v-row v-if="section.does_an_exception_apply">
        <v-col
          cols="12"
          sm="2"
        >
          <div class="reasonable-time-description">
            Reasonable time is presumptively 6 months from end of exception
          </div>
        </v-col>
        <v-col
          cols="12"
          sm="4"
        >
          <ComboDate
            :date="section.exception_end_date"
            label="Exception Ended"
            outlined
            :disabled="locked"
            :uncertain="section.exception_end_date_unknown === true"
            @update-box="updateField($event, 'exception_end_date_unknown')"
            @update-date="updateField($event, 'exception_end_date')"
          />
        </v-col>
        <v-col
          cols="6"
          sm="2"
        >
          <h5>Calculated Reasonable Time</h5>
          <div class="text-h6">
            {{ section.calculated_reasonable_time }}
          </div>
        </v-col>
        <v-col
          cols="6"
          sm="4"
        >
          <div>
            <strong>Disclaimer: </strong>The calculated date is 180 days after the end of the exception. It is not a substitute for individual analysis.
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="section.narrative"
            auto-grow
            outlined
            autocomplete="off"
            label="Narrative"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import moment from 'moment'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { alertHandler } from '@/alertHandler'

import ComboDate from '@/components/Fields/ComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'OneYearFilingDeadlinesCard',
  components: { ComboDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {},
    }
  },

  computed: {
    cardIdentifier () { return `one-year-filling-deadlines-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peOneYearFilingDeadlinesCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeOneYearFilingDeadlinesCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      return `(${this.section.name})`
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peOneYearFilingDeadlinesCardLock',
      'peParty',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeOneYearFilingDeadlinesCard(this.sectionIdentifier)
  },

  methods: {
    calculateTime (date, days) {
      if (date) return moment(date).add(days, 'days').format('MM/DD/YYYY')
    },

    calculatedFilingTime (section) {
      if (this.peParty?.date_last_entry) section.arrival_date = this.peParty.date_last_entry

      return this.calculateTime(section.arrival_date, 365)
    },

    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peOneYearFilingDeadlinesCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peOneYearFilingDeadlinesCardCreateUndo()
      else {
        return this.peOneYearFilingDeadlinesCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peOneYearFilingDeadlinesCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peOneYearFilingDeadlinesCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    async onSectionUnlock () {
      return this.setPeOneYearFilingDeadlinesCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    updateField (value, key) {
      this.section[key] = value
      if (value) this.section.calculated_reasonable_time = this.calculateTime(value, 180)
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peOneYearFilingDeadlinesCardCreateUndo',
      'setPeOneYearFilingDeadlinesCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeOneYearFilingDeadlinesCard',
      'peOneYearFilingDeadlinesCardCreate',
      'peOneYearFilingDeadlinesCardDelete',
      'peOneYearFilingDeadlinesCardRefresh',
      'peOneYearFilingDeadlinesCardUpdate',
    ]),
  },
}
</script>
